import { useTranslation } from "react-i18next";
import type { EditorialContent } from "@nh/shared";
import { cn } from "@/shared/lib/cn";
import { ProductBlockHeading } from "./ProductBlockHeading";
import { ProductEditorialIcon } from "./ProductEditorialIcon";

export type ProductSideBySideSection = NonNullable<EditorialContent["sideBySide"]>;
type ProductSide = ProductSideBySideSection["left"];

export interface ProductDetailSideBySideProps {
  section: ProductSideBySideSection;
  number?: string;
}

/**
 * Una de las dos caras del comparativo.
 *
 * Medidas del `SideCard` original: padding 28, radio 3, icono 22 px en
 * violet-pop con 14 px de aire debajo, título SANS 19px/600 a -0.01em con
 * 8 px debajo, y el texto a 13 px con interlínea 1.55 y peso 300.
 */
function ProductSideCard({ side, dark }: { side: ProductSide; dark: boolean }) {
  return (
    <li
      className={cn(
        "rounded-[3px] p-7",
        dark ? "bg-ds-ink" : "border border-ds-border bg-white",
      )}
    >
      {side.icon && (
        <span
          className={cn(
            /*
             * `flex` y no `block`: un `block` deja el interlineado de la
             * línea de texto vacía debajo del trazo, y los 14 px de aire
             * del original se convertían en veinte y pico.
             */
            "mb-3.5 flex",
            /*
             * En claro el icono va en violet-pop, como el original. En
             * oscuro sube a `lilac`: violet-pop sobre `ink` da 2.78:1, y
             * aunque sea un icono, el par de tarjetas se lee como una
             * comparación y la de la izquierda no puede verse apagada.
             */
            dark ? "text-ds-lilac" : "text-ds-violet-pop",
          )}
        >
          <ProductEditorialIcon name={side.icon} variant="step" />
        </span>
      )}

      <h3
        className={cn(
          "text-[19px] font-semibold leading-[1.25] tracking-[-0.01em]",
          dark ? "text-white" : "text-ds-ink",
        )}
      >
        {side.title}
      </h3>

      {side.description && (
        <p
          className={cn(
            "mt-2 text-[13px] font-light leading-[1.55]",
            dark ? "text-ds-lilac" : "text-ds-ink-3",
          )}
        >
          {side.description}
        </p>
      )}
    </li>
  );
}

/**
 * El comparativo de dos caras — "09 / Pareja conceptual" en el original.
 *
 * ── La pareja es OSCURA + CLARA, no dos iguales ──────────────────────
 * El original lo pinta así: `<SideCard left dark />` y `<SideCard right />`.
 * El contraste entre las dos tarjetas ES el bloque; dos tarjetas blancas
 * una al lado de la otra serían dos tarjetas, no una comparación.
 *
 * ── Qué hace `theme`, ya que el original no lo usa ───────────────────
 * El contrato trae `sideBySide.theme` y el renderizador original lo
 * IGNORA: sus dos caras están fijas. Ignorarlo aquí en silencio dejaría
 * un campo del formulario que no hace nada, así que se le da el
 * significado más corto que no inventa un diseño nuevo: `"dark"` —lo que
 * no se dice— es la pareja del original, y `"light"` apaga la tarjeta
 * oscura y deja las dos claras, para el editorial que no quiere el
 * golpe de contraste.
 *
 * ── Medidas del bloque ───────────────────────────────────────────────
 * Rótulo, título con 28 px debajo, y las dos tarjetas en una columna que
 * se parte en dos a partir de `md`, con 12 px entre ellas.
 */
export function ProductDetailSideBySide({
  section,
  number,
}: ProductDetailSideBySideProps) {
  const { t } = useTranslation();
  const pairDark = (section.theme ?? "dark") === "dark";

  return (
    <section className="grid gap-7">
      <ProductBlockHeading
        eyebrow={t("Pareja conceptual")}
        number={number}
        title={section.title}
      />

      <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <ProductSideCard side={section.left} dark={pairDark} />
        <ProductSideCard side={section.right} dark={false} />
      </ul>
    </section>
  );
}
